import { useMemo } from 'react'

import {
  useModals, useNumericParams, useSetState,
} from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import handleCallbackAction from '@functions/handleCallbackAction'
import ArtworkBuilderSelectionModal from '@modals/ArtworkBuilderSelectionModal/ArtworkBuilderSelectionModal'
import AssignCampaignQuoteItemDatesModal from '@modals/AssignCampaignQuoteItemDatesModal'
import BulkUpdateCampaignQuoteInvalidPricesModal from '@modals/BulkUpdateCampaignQuoteInvalidPricesModal'
import CanvaArtworkingLoadingModal from '@modals/CanvaArtworkingLoadingModal/CanvaArtworkingLoadingModal'
import CanvaTemplateSelectionModal from '@modals/CanvaTemplateSelectionModal/CanvaTemplateSelectionModal'
import ConfirmCampaignQuotePricingModal from '@modals/ConfirmCampaignQuotePricingModal'
import CreateCampaignContactModal from '@modals/CreateCampaignContactModal'
import CreateFinanceApplicationModal from '@modals/CreateFinanceApplicationModal'
import CreateOrEditCampaignContributionModal from '@modals/CreateOrEditCampaignContributionModal'
import CreateOrEditCampaignQuoteItemModal from '@modals/CreateOrEditCampaignQuoteItemModal'
import CreateOrEditCampaignQuoteModal from '@modals/CreateOrEditCampaignQuoteModal'
import CreateOrEditCampaignQuoteSectionModal from '@modals/CreateOrEditCampaignQuoteSectionModal'
import CreateOrEditContactModal from '@modals/CreateOrEditContactModal'
import CreateSubjectConversationModal from '@modals/CreateSubjectConversationModal'
import EditCampaignDatesModal from '@modals/EditCampaignDatesModal'
import EditCampaignQuoteSectionSortModal from '@modals/EditCampaignQuoteSectionSortModal'
import EditItemOptionsModal from '@modals/EditItemOptionsModal'
import ManageActivityModal from '@modals/ManageActivityModal'
import NewOrderModal from '@modals/NewOrderModal'
import SaveQuoteAsTemplateModal from '@modals/SaveQuoteAsTemplateModal'
import SelectEmailRecipientModal from '@modals/SelectEmailRecipientModal'
import SelectPublicationEditionModal from '@modals/SelectPublicationEditionModal'

import defaultRequestOptions from '@sections/Client/packs/Campaign/defaultRequestOptions'

import type { SendQuoteForApprovalPayload } from '@hooks/useCampaignQuote'
import type {
  ActivityModel,
  CampaignContactModel,
  CampaignModel,
  CampaignQuoteItemModel,
  CampaignQuoteModel,
  CampaignQuoteTemplateModel,
  ContactModel,
  FinanceApplicationModel,
} from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showArtworkBuilderSelectionModal: false,
  showAssignCampaignQuoteItemDatesModal: false,
  showBulkUpdateCampaignQuoteInvalidPricesModal: false,
  showCanvaArtworkingLoadingModal: false,
  showCanvaTemplateSelectionModal: false,
  showConfirmCampaignQuotePricingModal: false,
  showCreateCampaignContactModal: false,
  showCreateFinanceApplicationModal: false,
  showCreateOrEditCampaignContributionModal: false,
  showCreateOrEditCampaignQuoteItemModal: false,
  showCreateOrEditCampaignQuoteModal: false,
  showCreateOrEditCampaignQuoteSectionModal: false,
  showCreateOrEditContactModal: false,
  showCreateSubjectConversationModal: false,
  showEditCampaignDatesModal: false,
  showEditCampaignQuoteSectionSortModal: false,
  showEditItemOptionsModal: false,
  showManageActivityModal: false,
  showNewOrderModal: false,
  showSaveQuoteAsTemplateModal: false,
  showSelectEmailRecipientModal: false,
  showSelectPublicationEditionModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    ArtworkBuilderSelectionModal :  {
        closeModal: () => setState({ showArtworkBuilderSelectionModal: false})
    },
    AssignCampaignQuoteItemDatesModal: {
      closeModal: () => setState({ showAssignCampaignQuoteItemDatesModal: false }),
    },
    BulkUpdateCampaignQuoteInvalidPricesModal: {
      closeModal: () => setState({ showBulkUpdateCampaignQuoteInvalidPricesModal: false }),
    },
    CanvaArtworkingLoadingModal: {
      closeModal: () => setState({ showCanvaArtworkingLoadingModal: false }),
    },
    CanvaTemplateSelectionModal: {
      closeModal: () => setState({ showCanvaTemplateSelectionModal: false }),
    },
    CreateCampaignContactModal: {
      closeModal: () => setState({ showCreateCampaignContactModal: false }),
      createCampaignContact: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ConfirmCampaignQuotePricingModal: {
      approveCampaignQuote: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showConfirmCampaignQuotePricingModal: false }),
    },
    CreateFinanceApplicationModal: {
      closeModal: () => setState({ showCreateFinanceApplicationModal: false }),
      createFinanceApplication: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditCampaignContributionModal: {
      closeModal: () => setState({ showCreateOrEditCampaignContributionModal: false }),
      createCampaignContribution: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCampaignContribution: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditCampaignQuoteItemModal: {
      closeModal: () => setState({ showCreateOrEditCampaignQuoteItemModal: false }),
      createCampaignQuoteItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      duplicateCampaignQuoteItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCampaignQuoteItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditCampaignQuoteModal: {
      closeModal: () => setState({ showCreateOrEditCampaignQuoteModal: false }),
      updateCampaignQuote: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditCampaignQuoteSectionModal: {
      closeModal: () => setState({ showCreateOrEditCampaignQuoteSectionModal: false }),
      updateCampaignQuoteSection: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditContactModal: {
      closeModal: () => setState({ showCreateOrEditContactModal: false }),
      createContact: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      createContactUser: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      shareMobileLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateContact: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateSubjectConversationModal: {
      closeModal: () => setState({ showCreateSubjectConversationModal: false }),
      createSubjectConversation: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    EditCampaignDatesModal: {
      closeModal: () => setState({ showEditCampaignDatesModal: false }),
      closeAllModals: () => setState({
        showAssignCampaignQuoteItemDatesModal: false,
        showEditCampaignDatesModal: false,
      }),
      updateCampaign: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    EditCampaignQuoteSectionSortModal: {
      closeModal: () => setState({ showEditCampaignQuoteSectionSortModal: false }),
      updateCampaignQuoteSectionSort: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    EditItemOptionsModal: {
      closeModal: () => setState({ showEditItemOptionsModal: false }),
      closeAllModals: () => setState({
        showBulkUpdateCampaignQuoteInvalidPricesModal: false,
        showEditItemOptionsModal: false,
      }),
      updateItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageActivityModal: {
      closeModal: () => setState({ showManageActivityModal: false }),
    },
    NewOrderModal: {
      addItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showNewOrderModal: false }),
    },
    SaveQuoteAsTemplateModal: {
      closeModal: () => setState({ showSaveQuoteAsTemplateModal: false }),
      createCampaignQuoteTemplateFromQuote: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    SelectEmailRecipientModal: {
      closeModal: () => setState({ showSelectEmailRecipientModal: false }),
    },
    SelectPublicationEditionModal: {
      closeModal: () => setState({ showSelectPublicationEditionModal: false }),
    },
  }

  return componentCallbacks[component] || {}
}

type DetailsProps = {
  campaign: CampaignModel,
}

const Details = (props: DetailsProps) => {
  const { campaign } = props

  const [state, setState] = useSetState(defaultState)
  const {
    showArtworkBuilderSelectionModal,
    showAssignCampaignQuoteItemDatesModal,
    showBulkUpdateCampaignQuoteInvalidPricesModal,
    showCanvaArtworkingLoadingModal,
    showCanvaTemplateSelectionModal,
    showConfirmCampaignQuotePricingModal,
    showCreateCampaignContactModal,
    showCreateFinanceApplicationModal,
    showCreateOrEditCampaignContributionModal,
    showCreateOrEditCampaignQuoteItemModal,
    showCreateOrEditCampaignQuoteModal,
    showCreateOrEditCampaignQuoteSectionModal,
    showCreateOrEditContactModal,
    showCreateSubjectConversationModal,
    showEditCampaignDatesModal,
    showEditCampaignQuoteSectionSortModal,
    showEditItemOptionsModal,
    showManageActivityModal,
    showNewOrderModal,
    showSaveQuoteAsTemplateModal,
    showSelectEmailRecipientModal,
    showSelectPublicationEditionModal,
  } = state

  const { campaignQuoteId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showAssignCampaignQuoteItemDatesModal: (payload: { campaignQuote: CampaignQuoteModel }) => {
        setModalData('AssignCampaignQuoteItemDatesModal', payload)
        setState({ showAssignCampaignQuoteItemDatesModal: true })
      },
      showBulkUpdateCampaignQuoteInvalidPricesModal: (payload: object) => {
        setModalData('BulkUpdateCampaignQuoteInvalidPricesModal', payload)
        setState({ showBulkUpdateCampaignQuoteInvalidPricesModal: true })
      },
      closeCanvaArtworkingLoadingModal: () => {
        setState({ showCanvaArtworkingLoadingModal: false })
      },
      showCanvaArtworkingLoadingModal: (payload: { paragraph?: string, title?: string }) => {
        setModalData('CanvaArtworkingLoadingModal', payload)
        setState({ showCanvaArtworkingLoadingModal: true })
      },
      showCanvaTemplateSelectionModal: (payload: object) => {
        setModalData('CanvaTemplateSelectionModal', payload)
        setState({ showCanvaTemplateSelectionModal: true })
      },
      showCreateCampaignContactModal: (payload: { campaignContact: CampaignContactModel }) => {
        setModalData('CreateCampaignContactModal', payload)
        setState({ showCreateCampaignContactModal: true })
      },
      showConfirmCampaignQuotePricingModal: (payload: object) => {
        setModalData('ConfirmCampaignQuotePricingModal', payload)
        setState({ showConfirmCampaignQuotePricingModal: true })
      },
      showCreateFinanceApplicationModal: (payload: { financeApplication: FinanceApplicationModel }) => {
        setModalData('CreateFinanceApplicationModal', payload)
        setState({ showCreateFinanceApplicationModal: true })
      },
      showCreateOrEditCampaignContributionModal: (payload: object) => {
        setModalData('CreateOrEditCampaignContributionModal', payload)
        setState({ showCreateOrEditCampaignContributionModal: true })
      },
      showCreateOrEditCampaignQuoteItemModal: (payload: Partial<CampaignQuoteItemModel>) => {
        setModalData('CreateOrEditCampaignQuoteItemModal', payload)
        setState({ showCreateOrEditCampaignQuoteItemModal: true })
      },
      showCreateOrEditCampaignQuoteModal: (payload: Partial<CampaignQuoteModel>) => {
        setModalData('CreateOrEditCampaignQuoteModal', payload)
        setState({ showCreateOrEditCampaignQuoteModal: true })
      },
      showCreateOrEditCampaignQuoteSectionModal: (payload: object) => {
        setModalData('CreateOrEditCampaignQuoteSectionModal', payload)
        setState({ showCreateOrEditCampaignQuoteSectionModal: true })
      },
      showCreateOrEditContactModal: (payload: { contact: ContactModel }) => {
        setModalData('CreateOrEditContactModal', payload)
        setState({ showCreateOrEditContactModal: true })
      },
      showCreateSubjectConversationModal: (payload: SendQuoteForApprovalPayload) => {
        setModalData('CreateSubjectConversationModal', payload)
        setState({ showCreateSubjectConversationModal: true })
      },
      showEditCampaignDatesModal: (payload: { campaign: CampaignModel }) => {
        setModalData('EditCampaignDatesModal', payload)
        setState({ showEditCampaignDatesModal: true })
      },
      showEditItemOptionsModal: (payload: object) => {
        setModalData('EditItemOptionsModal', payload)
        setState({ showEditItemOptionsModal: true })
      },
      showEditCampaignQuoteSectionSortModal: (payload: object) => {
        setModalData('EditCampaignQuoteSectionSortModal', payload)
        setState({ showEditCampaignQuoteSectionSortModal: true })
      },
      showManageActivityModal: (payload: { activity: ActivityModel }) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
      showNewOrderModal: (payload: object) => {
        setModalData('NewOrderModal', payload)
        setState({ showNewOrderModal: true })
      },
      showSaveQuoteAsTemplateModal: (payload: Partial<CampaignQuoteTemplateModel>) => {
        setModalData('SaveQuoteAsTemplateModal', payload)
        setState({ showSaveQuoteAsTemplateModal: true })
      },
      showSelectEmailRecipientModal: (payload: { campaignQuote: CampaignQuoteModel }) => {
        setModalData('SelectEmailRecipientModal', payload)
        setState({ showSelectEmailRecipientModal: true })
      },
      showSelectPublicationEditionModal: (payload: object) => {
        setModalData('SelectPublicationEditionModal', payload)
        setState({ showSelectPublicationEditionModal: true })
      },
      showArtworkBuilderSelectionModal: (payload: object) => {
        setModalData('ArtworkBuilderSelectionModal', payload)
        setState({ showArtworkBuilderSelectionModal: true })
      },
      updateCampaignQuote: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent campaign={campaign} campaignQuoteId={campaignQuoteId} />
        <ArtworkBuilderSelectionModal
          callbacks={callbacks('ArtworkBuilderSelectionModal', setState)}
          showModal={showArtworkBuilderSelectionModal}
        />
        <AssignCampaignQuoteItemDatesModal
          callbacks={callbacks('AssignCampaignQuoteItemDatesModal', setState)}
          showModal={showAssignCampaignQuoteItemDatesModal}
        />

        <BulkUpdateCampaignQuoteInvalidPricesModal
          // @ts-expect-error TS2322
          callbacks={callbacks('BulkUpdateCampaignQuoteInvalidPricesModal', setState)}
          showModal={showBulkUpdateCampaignQuoteInvalidPricesModal}
        />

        <CanvaArtworkingLoadingModal
          callbacks={callbacks('CanvaArtworkingLoadingModal', setState)}
          showModal={showCanvaArtworkingLoadingModal}
        />

        <CanvaTemplateSelectionModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CanvaTemplateSelectionModal', setState)}
          showModal={showCanvaTemplateSelectionModal}
        />

        <CreateCampaignContactModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateCampaignContactModal', setState)}
          showModal={showCreateCampaignContactModal}
        />

        <ConfirmCampaignQuotePricingModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ConfirmCampaignQuotePricingModal', setState)}
          showModal={showConfirmCampaignQuotePricingModal}
        />

        <CreateFinanceApplicationModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateFinanceApplicationModal', setState)}
          showModal={showCreateFinanceApplicationModal}
        />

        <CreateOrEditCampaignContributionModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCampaignContributionModal', setState)}
          showModal={showCreateOrEditCampaignContributionModal}
        />

        <CreateOrEditCampaignQuoteItemModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCampaignQuoteItemModal', setState)}
          requestOptions={{
            ...defaultRequestOptions.campaignQuoteItem,
          }}
          showModal={showCreateOrEditCampaignQuoteItemModal}
        />

        <CreateOrEditCampaignQuoteModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCampaignQuoteModal', setState)}
          showModal={showCreateOrEditCampaignQuoteModal}
        />

        <CreateOrEditCampaignQuoteSectionModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCampaignQuoteSectionModal', setState)}
          showModal={showCreateOrEditCampaignQuoteSectionModal}
        />

        <CreateOrEditContactModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditContactModal', setState)}
          showModal={showCreateOrEditContactModal}
        />

        <CreateSubjectConversationModal
          actionText="Send for Approval"
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateSubjectConversationModal', setState)}
          showModal={showCreateSubjectConversationModal}
        />

        <EditCampaignDatesModal
          // @ts-expect-error TS2322
          callbacks={callbacks('EditCampaignDatesModal', setState)}
          showModal={showEditCampaignDatesModal}
        />

        <EditCampaignQuoteSectionSortModal
          // @ts-expect-error TS2322
          callbacks={callbacks('EditCampaignQuoteSectionSortModal', setState)}
          showModal={showEditCampaignQuoteSectionSortModal}
        />

        <EditItemOptionsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('EditItemOptionsModal', setState)}
          requestOptions={{
            include_campaign_quote_item_quote_total: true,
          }}
          showModal={showEditItemOptionsModal}
        />

        <ManageActivityModal
          callbacks={callbacks('ManageActivityModal', setState)}
          showModal={showManageActivityModal}
        />

        <NewOrderModal
          callbacks={callbacks('NewOrderModal', setState)}
          showModal={showNewOrderModal}
        />

        <SaveQuoteAsTemplateModal
          // @ts-expect-error TS2322
          callbacks={callbacks('SaveQuoteAsTemplateModal', setState)}
          showModal={showSaveQuoteAsTemplateModal}
        />

        <SelectEmailRecipientModal
          // @ts-expect-error TS2322
          callbacks={callbacks('SelectEmailRecipientModal', setState)}
          showModal={showSelectEmailRecipientModal}
        />

        <SelectPublicationEditionModal
          callbacks={callbacks('SelectPublicationEditionModal', setState)}
          showModal={showSelectPublicationEditionModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
